/**
 * 블로그 상세 하단 "함께 보면 좋은 정보" 링크 계산 — 순수 함수(API 호출 없음, SSR 에 그대로 나온다).
 *
 * 영구지침: docs/develop/seo/blog-internal-links-spec.md
 */
// 상대 경로 import — `npm test`(tsx --test)는 '@/' 별칭을 해석하지 못한다.
import { OFFICIAL_SIDOS, SIDO_ALIAS_TO_OFFICIAL } from './regions'

export interface RelatedLink {
  label: string
  to: string
}

const CALCULATOR: RelatedLink = { label: '요양비용 계산기', to: '/care-calculator' }
const SIMULATOR: RelatedLink = { label: '요양원 비용 시뮬레이터', to: '/cost-simulator' }
const COST_GUIDE: RelatedLink = { label: '요양 비용 가이드', to: '/guide/cost' }
const GRADE_GUIDE: RelatedLink = { label: '장기요양등급 안내', to: '/guide/care-grade' }
const SELF_ASSESSMENT: RelatedLink = { label: '등급 자가진단', to: '/guide/self-assessment' }
const FACILITY_TYPES: RelatedLink = { label: '요양시설 종류 비교', to: '/guide/facility-types' }
const CHECKLIST: RelatedLink = { label: '시설 방문 체크리스트', to: '/checklist' }
const RESOURCES: RelatedLink = { label: '보호자 지원 자료', to: '/guide/resources' }
const FIND_BY_REGION: RelatedLink = { label: '지역별 요양시설 찾기', to: '/facilities' }

/** 카테고리 → 도구·가이드 링크 (영구지침 §3) */
const TOOL_LINKS: Record<string, RelatedLink[]> = {
  cost: [CALCULATOR, SIMULATOR, COST_GUIDE],
  policy: [GRADE_GUIDE, SELF_ASSESSMENT, CALCULATOR],
  'facility-selection': [FACILITY_TYPES, CHECKLIST, FIND_BY_REGION],
  guardian: [CHECKLIST, RESOURCES, FACILITY_TYPES],
  region: [FIND_BY_REGION, CALCULATOR],
}

export function toolLinksFor(category: string | null | undefined): RelatedLink[] {
  return (category && TOOL_LINKS[category]) || [FIND_BY_REGION, CALCULATOR]
}

const MAX_REGION_LINKS = 3
const CONTENT_SCAN_LENGTH = 2000

/**
 * 약칭 뒤에 허용하는 것 — 문자열 끝·한글 아닌 문자·조사.
 * `시` 는 넣지 않는다: `광주시`(경기도)·`세종시` 처럼 다른 지역이거나 모호하다.
 */
const ALIAS_FOLLOW = '(?=$|[^가-힣]|은|는|이|가|에|의|도|권|지역)'

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

const ALIAS_PATTERNS = Object.entries(SIDO_ALIAS_TO_OFFICIAL).map(([alias, official]) => ({
  official,
  regex: new RegExp(`(^|[^가-힣])(${escapeRegExp(alias)})${ALIAS_FOLLOW}`, 'g'),
}))

/**
 * 글에 언급된 시·도 → `/facilities/{정식 시·도}` 링크, 최대 3개, 등장 순서 (영구지침 §4).
 * 언급이 없으면 빈 배열 — 17개 전부를 나열하는 문맥 없는 링크는 만들지 않는다.
 */
export function regionLinksFor(post: {
  title: string
  keywords?: string[] | null
  content?: string | null
}): RelatedLink[] {
  const text = [post.title, (post.keywords ?? []).join(' '), (post.content ?? '').slice(0, CONTENT_SCAN_LENGTH)]
    .join(' ')

  const firstIndex = new Map<string, number>()
  const record = (official: string, index: number) => {
    const prev = firstIndex.get(official)
    if (prev === undefined || index < prev) firstIndex.set(official, index)
  }

  for (const official of OFFICIAL_SIDOS) {
    const idx = text.indexOf(official)
    if (idx >= 0) record(official, idx)
  }
  for (const { official, regex } of ALIAS_PATTERNS) {
    regex.lastIndex = 0
    const m = regex.exec(text)
    if (m) record(official, m.index + (m[1]?.length ?? 0))
  }

  return [...firstIndex.entries()]
    .sort((a, b) => a[1] - b[1])
    .slice(0, MAX_REGION_LINKS)
    .map(([official]) => ({ label: `${official} 요양시설 비교`, to: `/facilities/${official}` }))
}
